import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Lock,
  Mail,
  Link as LinkIcon,
  Bell,
  Calendar,
  Users,
  MessageSquare,
  Tag,
  Eye,
  Activity,
  MapPin,
  Ban,
  Ruler,
  Trophy,
  Globe,
  Moon,
  HelpCircle,
  Mail as MailIcon,
  Flag,
  FileText,
  Shield,
  Info,
  Star,
} from 'lucide-react';
import { Heading, Text, Stack, Container } from '@/components/ui/index.js';
import { useSettingsStore } from '@/features/profile/store/useSettingsStore.js';
import { useAuthStore } from '@/stores/authStore.js';
import { useUIStore } from '@/stores/uiStore.js';
import { SettingsSection } from './components/SettingsSection/SettingsSection.jsx';
import { SettingsItem } from './components/SettingsItem/SettingsItem.jsx';
import { ToggleItem } from './components/ToggleItem/ToggleItem.jsx';
import { SelectItem } from './components/SelectItem/SelectItem.jsx';
import { ConfirmDialog } from './components/ConfirmDialog/ConfirmDialog.jsx';
import styles from './SettingsPage.module.css';

// Social provider icons
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);
const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);
const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// Select options
const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public' },
  { value: 'friends', label: 'Friends Only' },
  { value: 'private', label: 'Private' },
];

const DISTANCE_OPTIONS = [
  { value: 'miles', label: 'Miles' },
  { value: 'km', label: 'Kilometers' },
];

const SPORT_OPTIONS = [
  { value: 'all', label: 'All Sports' },
  { value: 'basketball', label: 'Basketball' },
  { value: 'soccer', label: 'Soccer' },
  { value: 'tennis', label: 'Tennis' },
  { value: 'running', label: 'Running' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
];

const THEME_OPTIONS = [
  { value: 'dark', label: 'Dark' },
  { value: 'light', label: 'Light' },
  { value: 'system', label: 'System' },
];

const LOCATION_PRECISION_OPTIONS = [
  { value: 'precise', label: 'Precise' },
  { value: 'approximate', label: 'Approximate' },
];

/**
 * SettingsPage - User settings and preferences
 * 
 * Account preferences, notification settings, privacy controls,
 * and app configuration options.
 */
export function SettingsPage() {
  const navigate = useNavigate();
  const { logout } = useAuthStore();
  const { showSuccess, showInfo } = useUIStore();
  const {
    account,
    notifications,
    privacy,
    preferences,
    appInfo,
    setNotification,
    setAllNotifications,
    setPrivacy,
    setPreference,
    isLoading,
  } = useSettingsStore();

  // Dialog states
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Handle back navigation
  const handleBack = () => {
    navigate('/profile');
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    setLogoutDialogOpen(false);
    showSuccess('Logged out successfully');
    navigate('/login');
  };

  // Handle delete account
  const handleDeleteAccount = async () => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setDeleteDialogOpen(false);
    showSuccess('Account deleted');
    navigate('/');
  };

  // Handle notification master toggle
  const handlePushToggle = (enabled) => {
    setAllNotifications(enabled);
    showInfo(enabled ? 'Notifications enabled' : 'Notifications disabled');
  };

  // Handle settings navigation (placeholder)
  const handleNavigate = (path) => {
    showInfo('Coming soon');
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <Container size="lg" padding={false} className={styles.headerContent}>
          <button
            type="button"
            onClick={handleBack}
            className={styles.backButton}
            aria-label="Go back"
          >
            <ArrowLeft size={24} />
          </button>
          <Heading level={1} size="lg" color="primary">
            Settings
          </Heading>
          <div className={styles.headerSpacer} />
        </Container>
      </header>

      {/* Content */}
      <main className={styles.content}>
        <Container size="lg" className={styles.container}>
          <Stack direction="column" gap={6}>
            {/* Account Section */}
            <SettingsSection title="Account">
              <SettingsItem
                icon={User}
                label="Edit Profile"
                onClick={() => handleNavigate('/profile/edit')}
              />
              <SettingsItem
                icon={Lock}
                label="Change Password"
                onClick={() => handleNavigate('/password')}
              />
              <SettingsItem
                icon={Mail}
                label="Email Preferences"
                value={account.email}
                onClick={() => handleNavigate('/email')}
              />
              <div className={styles.connectedAccounts}>
                <SettingsItem
                  icon={LinkIcon}
                  label="Connected Accounts"
                  showChevron={false}
                />
                <div className={styles.accountList}>
                  <div className={styles.accountItem}>
                    <GoogleIcon />
                    <Text size="sm" color="secondary">Google</Text>
                    <div className={`${styles.status} ${account.connectedAccounts.google ? styles.connected : ''}`}>
                      {account.connectedAccounts.google ? 'Connected' : 'Not connected'}
                    </div>
                  </div>
                  <div className={styles.accountItem}>
                    <AppleIcon />
                    <Text size="sm" color="secondary">Apple</Text>
                    <div className={`${styles.status} ${account.connectedAccounts.apple ? styles.connected : ''}`}>
                      {account.connectedAccounts.apple ? 'Connected' : 'Not connected'}
                    </div>
                  </div>
                  <div className={styles.accountItem}>
                    <FacebookIcon />
                    <Text size="sm" color="secondary">Facebook</Text>
                    <div className={`${styles.status} ${account.connectedAccounts.facebook ? styles.connected : ''}`}>
                      {account.connectedAccounts.facebook ? 'Connected' : 'Not connected'}
                    </div>
                  </div>
                </div>
              </div>
            </SettingsSection>

            {/* Notifications Section */}
            <SettingsSection title="Notifications">
              <ToggleItem
                icon={Bell}
                label="Push Notifications"
                description="Master toggle for all notifications"
                checked={notifications.pushEnabled}
                onChange={handlePushToggle}
              />
              <ToggleItem
                icon={Calendar}
                label="Event Reminders"
                description="Get notified about upcoming events"
                checked={notifications.eventReminders}
                onChange={(v) => setNotification('eventReminders', v)}
                disabled={!notifications.pushEnabled}
              />
              <ToggleItem
                icon={Users}
                label="Friend Activity"
                description="When friends join or create events"
                checked={notifications.friendActivity}
                onChange={(v) => setNotification('friendActivity', v)}
                disabled={!notifications.pushEnabled}
              />
              <ToggleItem
                icon={MessageSquare}
                label="Messages"
                description="Direct messages and chat notifications"
                checked={notifications.messages}
                onChange={(v) => setNotification('messages', v)}
                disabled={!notifications.pushEnabled}
              />
              <ToggleItem
                icon={Tag}
                label="Promotional"
                description="News, offers, and product updates"
                checked={notifications.promotional}
                onChange={(v) => setNotification('promotional', v)}
              />
            </SettingsSection>

            {/* Privacy Section */}
            <SettingsSection title="Privacy">
              <SelectItem
                icon={Eye}
                label="Profile Visibility"
                value={privacy.profileVisibility}
                options={VISIBILITY_OPTIONS}
                onChange={(v) => setPrivacy('profileVisibility', v)}
              />
              <ToggleItem
                icon={Activity}
                label="Activity Status"
                description="Show when you're active"
                checked={privacy.activityStatus}
                onChange={(v) => setPrivacy('activityStatus', v)}
              />
              <div className={styles.locationSettings}>
                <ToggleItem
                  icon={MapPin}
                  label="Location Sharing"
                  description="Share your location with others"
                  checked={privacy.locationSharing}
                  onChange={(v) => setPrivacy('locationSharing', v)}
                />
                {privacy.locationSharing && (
                  <div className={styles.precisionSelect}>
                    <SelectItem
                      label="Precision"
                      value={privacy.locationPrecision}
                      options={LOCATION_PRECISION_OPTIONS}
                      onChange={(v) => setPrivacy('locationPrecision', v)}
                    />
                  </div>
                )}
              </div>
              <SettingsItem
                icon={Ban}
                label="Blocked Users"
                value={`${privacy.blockedUsersCount} blocked`}
                onClick={() => handleNavigate('/blocked')}
              />
            </SettingsSection>

            {/* Preferences Section */}
            <SettingsSection title="Preferences">
              <SelectItem
                icon={Ruler}
                label="Distance Units"
                value={preferences.distanceUnit}
                options={DISTANCE_OPTIONS}
                onChange={(v) => setPreference('distanceUnit', v)}
              />
              <SelectItem
                icon={Trophy}
                label="Default Sport Filter"
                value={preferences.defaultSport}
                options={SPORT_OPTIONS}
                onChange={(v) => setPreference('defaultSport', v)}
              />
              <SelectItem
                icon={Globe}
                label="Language"
                value={preferences.language}
                options={LANGUAGE_OPTIONS}
                onChange={(v) => setPreference('language', v)}
              />
              <SelectItem
                icon={Moon}
                label="Theme"
                value={preferences.theme}
                options={THEME_OPTIONS}
                onChange={(v) => setPreference('theme', v)}
              />
            </SettingsSection>

            {/* Support Section */}
            <SettingsSection title="Support">
              <SettingsItem
                icon={HelpCircle}
                label="Help Center"
                external
                href="https://help.sportspots.com"
              />
              <SettingsItem
                icon={MailIcon}
                label="Contact Support"
                external
                href="mailto:support@sportspots.com"
              />
              <SettingsItem
                icon={Flag}
                label="Report a Problem"
                onClick={() => handleNavigate('/report')}
              />
              <SettingsItem
                icon={FileText}
                label="Terms of Service"
                external
                href="https://sportspots.com/terms"
              />
              <SettingsItem
                icon={Shield}
                label="Privacy Policy"
                external
                href="https://sportspots.com/privacy"
              />
            </SettingsSection>

            {/* About Section */}
            <SettingsSection title="About">
              <SettingsItem
                icon={Info}
                label="App Version"
                value={`v${appInfo.version}`}
                showChevron={false}
              />
              <SettingsItem
                icon={Info}
                label="Build Number"
                value={appInfo.buildNumber}
                showChevron={false}
              />
              <SettingsItem
                icon={Star}
                label="Rate App"
                external
                href="https://apps.apple.com/app/sportspots"
              />
            </SettingsSection>

            {/* Danger Zone Section */}
            <SettingsSection title="Danger Zone" variant="danger">
              <div className={styles.dangerZone}>
                <button
                  type="button"
                  onClick={() => setLogoutDialogOpen(true)}
                  className={styles.logoutButton}
                >
                  <Text size="base" weight="medium">Log Out</Text>
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteDialogOpen(true)}
                  className={styles.deleteButton}
                >
                  <Text size="base" weight="medium">Delete Account</Text>
                </button>
              </div>
            </SettingsSection>

            {/* Bottom spacing for mobile */}
            <div className={styles.bottomSpacer} />
          </Stack>
        </Container>
      </main>

      {/* Log Out Confirmation Dialog */}
      <ConfirmDialog
        isOpen={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleLogout}
        title="Log Out"
        message="Are you sure you want to log out? You'll need to sign in again to access your account."
        confirmLabel="Log Out"
        variant="logout"
        isLoading={isLoading}
      />

      {/* Delete Account Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteAccount}
        title="Delete Account"
        message="This action cannot be undone. This will permanently delete your account and remove all your data from our servers."
        confirmLabel="Delete Account"
        cancelLabel="Keep Account"
        variant="delete"
        isLoading={isLoading}
      />
    </div>
  );
}

export default SettingsPage;
